import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Enquiry.css';

gsap.registerPlugin(ScrollTrigger);

const TYPES = ['Residential', 'Commercial', 'Industrial', 'Government', 'Institutional'];

// Declared at module scope on purpose. Defining this inside Enquiry would make it
// a new component type on every render, so React would remount each field —
// stealing focus from inputs and swallowing the select's change event.
const Field = ({ label, error, children }) => (
  <div className={`field${error ? ' invalid' : ''}`}>
    <label>{label}</label>
    {children}
    {error && <span className="err">{error}</span>}
  </div>
);

export default function Enquiry() {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', location: '', project_type: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sendError, setSendError] = useState('');

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(leftRef.current, { opacity: 0, x: -24, duration: 0.9, ease: 'power3.out', scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' } });
      gsap.from(rightRef.current, { opacity: 0, x: 24, duration: 0.9, ease: 'power3.out', scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' } });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  // Clear the field's error here rather than in a separate onInput handler:
  // onInput fires before change on a <select>, so re-rendering from it would
  // tear the element down before onChange could run.
  const set = k => e => {
    const { value } = e.target;
    setForm(f => ({ ...f, [k]: value }));
    setErrors(er => (er[k] ? { ...er, [k]: '' } : er));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!/^\+?[\d\s\-]{7,15}$/.test(form.phone.trim())) e.phone = 'Valid number required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.project_type) e.project_type = 'Required';
    return e;
  };

  const submit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSending(true);
    setSuccess(false);
    setSendError('');

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));

      const response = await fetch('/mail.php', {
        method: 'POST',
        body: fd,
      });

      // A failed send returns JSON with the SMTP reason, so read the body
      // before deciding — response.ok alone hides why it broke.
      const result = await response.json().catch(() => null);
      if (!response.ok || result?.status !== 'ok') {
        throw new Error(result?.message || `Request failed (${response.status})`);
      }

      setSuccess(true);
      setForm({ name: '', phone: '', email: '', location: '', project_type: '', message: '' });
    } catch (error) {
      console.error(error);
      setSuccess(false);
      setSendError("Couldn't send your enquiry. Please try again or email info@csolar.cc");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section enq-section" id="enquiry" ref={sectionRef}>
      <div className="enq-layout">
        {/* Left */}
        <div className="enq-left" ref={leftRef}>
          <div>
            <p className="eyebrow enq-eyebrow">Start Your Project</p>
            <h2 className="enq-title">Let's<br />Build<br /><span className="light">Your Solar</span><br />Future.</h2>
          </div>
          <div className="enq-left-footer">
            <p>We respond within 24 hours.<br />No commitment required.</p>
            <span>C-SOLAR — Est. 2024</span>
          </div>
        </div>

        {/* Right */}
        <div className="enq-right" ref={rightRef}>
          <form onSubmit={submit} noValidate>
            <div className="form-grid">
              <Field label="Full Name" error={errors.name}>
                <input type="text" value={form.name} onChange={set('name')} placeholder="John Smith" />
              </Field>
              <Field label="Phone" error={errors.phone}>
                <input type="tel" value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" />
              </Field>
            </div>

            <Field label="Email Address" error={errors.email}>
              <input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" />
            </Field>

            <div className="form-grid">
              <Field label="City / Location" error={errors.location}>
                <input type="text" value={form.location} onChange={set('location')} placeholder="Chennai, Tamil Nadu" />
              </Field>
              <Field label="Project Type" error={errors.project_type}>
                <select value={form.project_type} onChange={set('project_type')}>
                  <option value="">Select</option>
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Message (optional)" error={errors.message}>
              <textarea value={form.message} onChange={set('message')} placeholder="Tell us about your project..." />
            </Field>

            <div className="submit-row">
              <p className="submit-note">All information is<br />kept confidential.</p>
              <button type="submit" className="submit-btn" disabled={sending}>
                {sending ? 'Sending...' : 'Send Enquiry'}
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path d="M1 6h10M6 1l5 5-5 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            {sendError && <p className="submit-error" role="alert">{sendError}</p>}
          </form>
        </div>
      </div>

      {/* Success */}
      {success && (
        <div className="success-overlay">
          <div className="success-line" />
          <h3>Thank You</h3>
          <p>Our team will contact you soon</p>
        </div>
      )}
    </section>
  );
}
